import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { successToast, errorToast } from '../../lib/helpers';
import { UserGroupIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

function AssignCust() {
    const { apir } = useContext(GlobalContext);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('All');
    const [operators, setOperators] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [customerLinks, setCustomerLinks] = useState([]);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [availableCustomers, setAvailableCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [operatorSearchTerm, setOperatorSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        document.title = 'Assign Customers - Sales Assist';
        return () => {
            document.title = 'Sales Assist';
        };
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const branchesResponse = await fetch(`${apir}/salesassist/cplusassist/branches`);
            const branchesData = await branchesResponse.json();
            setBranches(branchesData);

            const operatorsResponse = await fetch(`${apir}/salesassist/cplusassist/getOperators`);
            const operatorsData = await operatorsResponse.json();
            setOperators(operatorsData);

            const customersResponse = await fetch(`${apir}/salesassist/cplusassist/getCustomers`);
            const customersData = await customersResponse.json();
            setCustomers(customersData);

            const linksResponse = await fetch(`${apir}/salesassist/cplusassist/getCustLink`);
            const linksData = await linksResponse.json();
            setCustomerLinks(linksData);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            errorToast('Failed to load data');
            setLoading(false);
        }
    };

    const handleBranchChange = (event) => {
        setSelectedBranch(event.target.value);
        setSelectedOperator(null);
    };

    const handleOperatorSelect = (operator) => {
        setSelectedOperator(operator);
        const assignedCustomerIds = customerLinks
            .filter(link => link.Username === operator.Username)
            .flatMap(link => link.Customers);

        const filteredCustomers = customers.filter(customer => !assignedCustomerIds.includes(customer.KeyCode));
        setAvailableCustomers(filteredCustomers);
        setSearchTerm('');
    };

    const assignCustomer = async (customerId) => {
        const customer = customers.find(cust => cust.KeyCode === customerId);
        try {
            const custLink = customerLinks.find(link => link.Username === selectedOperator.Username);

            if (custLink) {
                const updatedCustomers = [...new Set([...custLink.Customers, customerId])];
                await fetch(`${apir}/salesassist/cplusassist/editCustLink/${custLink._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Username: selectedOperator.Username, Customers: updatedCustomers }),
                });
                successToast(`Assigned ${customer.Name} to ${selectedOperator.Name}`);
            } else {
                await fetch(`${apir}/salesassist/cplusassist/addCustLink`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Username: selectedOperator.Username, Customers: [customerId] }),
                });
                successToast(`Assigned ${customer.Name} to ${selectedOperator.Name}`);
            }

            fetchData();
        } catch (error) {
            console.error('Error assigning customer:', error);
            errorToast(`Failed to assign ${customer.Name}`);
        }
    };

    const removeCustomer = async (customerId) => {
        const customer = customers.find(cust => cust.KeyCode === customerId);
        const custLink = customerLinks.find(link => link.Username === selectedOperator.Username);

        if (!custLink) return;

        const updatedCustomers = custLink.Customers.filter(id => id !== customerId);

        try {
            if (updatedCustomers.length === 0) {
                await fetch(`${apir}/salesassist/cplusassist/deleteCustLink/${custLink._id}`, {
                    method: 'DELETE',
                });
                successToast(`Removed ${customer.Name} from ${selectedOperator.Name}`);
            } else {
                await fetch(`${apir}/salesassist/cplusassist/editCustLink/${custLink._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Username: selectedOperator.Username, Customers: updatedCustomers }),
                });
                successToast(`Removed ${customer.Name} from ${selectedOperator.Name}`);
            }

            fetchData();
        } catch (error) {
            console.error('Error removing customer:', error);
            errorToast(`Failed to remove ${customer.Name}`);
        }
    };

    const openCustomerModal = (operator) => {
        setSelectedOperator(operator);
        setModalSearchTerm('');
        setShowCustomerModal(true);
    };

    const closeCustomerModal = () => {
        setShowCustomerModal(false);
        setSelectedOperator(null);
    };

    const filteredOperators = operators
        .filter(op => selectedBranch === 'All' || op.Branch === selectedBranch)
        .filter(op => op.Name.toLowerCase().includes(operatorSearchTerm.toLowerCase()) ||
            op.Inits.toLowerCase().includes(operatorSearchTerm.toLowerCase()));

    const filteredCustomers = availableCustomers.filter(customer =>
        customer.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.KeyCode.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredModalCustomers = selectedOperator
        ? customerLinks
            .find(link => link.Username === selectedOperator.Username)?.Customers
            .map(customerId => customers.find(customer => customer.KeyCode === customerId))
            .filter(customer => customer && (customer.Name.toLowerCase().includes(modalSearchTerm.toLowerCase()) || customer.KeyCode.toLowerCase().includes(modalSearchTerm.toLowerCase()))
            ) : [];

    // Calculate statistics
    const totalOperators = filteredOperators.length;
    const totalAssigned = customerLinks.reduce((sum, link) => sum + (link.Customers?.length || 0), 0);
    const operatorsWithAssignments = customerLinks.filter(link => link.Customers?.length > 0).length;
    const avgCustomersPerOperator = totalOperators > 0 ? (totalAssigned / totalOperators).toFixed(1) : 0;

    // Get assigned customers for selected operator
    const assignedCustomersForOperator = selectedOperator
        ? customerLinks
            .find(link => link.Username === selectedOperator.Username)?.Customers
            .map(customerId => customers.find(customer => customer.KeyCode === customerId))
            .filter(customer => customer) || []
        : [];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-20">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading assignment data...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-20">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-purple-100">
                            <UserGroupIcon className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Customer Assignments</h1>
                            <p className="text-sm text-gray-600 mt-1">Manage customer assignments to operators</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="py-6">
                <div className="px-6">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Operators</div>
                            <div className="text-2xl font-bold text-gray-900">{totalOperators}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered by branch</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Assignments</div>
                            <div className="text-2xl font-bold text-gray-900">{totalAssigned}</div>
                            <div className="text-xs text-gray-500 mt-1">All customers</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Operators Assigned</div>
                            <div className="text-2xl font-bold text-gray-900">{operatorsWithAssignments}</div>
                            <div className="text-xs text-gray-500 mt-1">With customers</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Avg per Operator</div>
                            <div className="text-2xl font-bold text-gray-900">{avgCustomersPerOperator}</div>
                            <div className="text-xs text-gray-500 mt-1">Average assignments</div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                                <select
                                    value={selectedBranch}
                                    onChange={handleBranchChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    <option value="All">All Branches</option>
                                    {branches.map((branch, index) => (
                                        <option key={index} value={branch.Branch}>{branch.Branch}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Search Operators</label>
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="Search by name or initials..."
                                        value={operatorSearchTerm}
                                        onChange={(e) => setOperatorSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Operator</label>
                                <select
                                    value={selectedOperator ? selectedOperator.Username : ''}
                                    onChange={(e) => {
                                        const operator = operators.find(op => op.Username === e.target.value);
                                        if (operator) handleOperatorSelect(operator);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    <option value="">-- Select Operator --</option>
                                    {filteredOperators.map(operator => (
                                        <option key={operator._id} value={operator.Username}>
                                            {operator.Name} ({operator.Inits}) - {operator.Branch}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Assignment Table */}
                    {selectedOperator && (
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
                            <div className="p-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Assignments for {selectedOperator.Name} ({selectedOperator.Inits})
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">{selectedOperator.Position} - {selectedOperator.Branch}</p>
                            </div>

                            <div className="p-4">
                                {/* Assign New Customer */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign New Customer</label>
                                    <div className="relative">
                                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            placeholder="Search for a customer by name or KeyCode..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && filteredCustomers.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                                {filteredCustomers.map(customer => (
                                                    <div
                                                        key={customer.KeyCode}
                                                        className="p-3 hover:bg-purple-50 cursor-pointer flex justify-between items-center border-b border-gray-100 last:border-b-0"
                                                        onClick={() => {
                                                            assignCustomer(customer.KeyCode);
                                                            setSearchTerm('');
                                                        }}
                                                    >
                                                        <div>
                                                            <div className="font-medium text-gray-900">{customer.Name}</div>
                                                            <div className="text-sm text-gray-500">{customer.KeyCode}</div>
                                                        </div>
                                                        <PlusIcon className="w-5 h-5 text-purple-600" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Assigned Customers Table */}
                                {assignedCustomersForOperator.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Name</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key Code</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {assignedCustomersForOperator.map(customer => (
                                                    <tr key={customer.KeyCode} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {customer.Name}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {customer.KeyCode}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                                                            <button
                                                                onClick={() => removeCustomer(customer.KeyCode)}
                                                                className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                        <p>No customers assigned to this operator yet.</p>
                                        <p className="text-sm mt-1">Use the search above to assign customers.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!selectedOperator && (
                        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                            <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Operator</h3>
                            <p className="text-gray-500">Choose an operator from the dropdown above to view and manage their customer assignments.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Customer Modal */}
            {showCustomerModal && selectedOperator && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Customers Assigned to {selectedOperator.Name}</h3>
                                <p className="text-sm text-gray-500 mt-1">{selectedOperator.Position} - {selectedOperator.Branch}</p>
                            </div>
                            <button
                                onClick={closeCustomerModal}
                                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <div className="mb-4">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="Search for a customer..."
                                        value={modalSearchTerm}
                                        onChange={(e) => setModalSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            {filteredModalCustomers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No customers found</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {filteredModalCustomers.map(customer => (
                                        <div key={customer.KeyCode} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div>
                                                <div className="font-medium text-gray-900">{customer.Name}</div>
                                                <div className="text-sm text-gray-500">{customer.KeyCode}</div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    removeCustomer(customer.KeyCode);
                                                    closeCustomerModal();
                                                }}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AssignCust;
